/**
 * `AgentProvider` — the terminal state machine, ported from the parked
 * `AgentProvider.tsx` and rewired onto `@paradigm/terminal-core` factories
 * plus a host-supplied `TerminalConfig` (read from context).
 *
 * Responsibilities (all preserved from the original):
 *  - Connection lifecycle (connect / disconnect / reconnect-on-account-change)
 *  - Auth + self-service pairing (delegated to `createOpenClawClient`)
 *  - Per-session frame routing (runId → session ownership + background
 *    buffering) so an in-flight run keeps writing to its originating chat
 *  - Processing indicator, `/model` echo suppression
 *  - Sessions / folders CRUD, hydrate-on-account-change
 *
 * Remote session-sync is NATIVE (not host-injected): on hydration we pull the
 * gateway's `/sync/v1` snapshot, merge it into the chat-history store, and push
 * local changes. It degrades to local-only when the gateway has no sync API.
 */
import {
  generateSessionId,
  SESSION_TITLE_MAX_LEN,
  createOpenClawClient,
  createSessionSync,
  hydrateFromRemote,
  fetchAndMergeSessionFromRemote,
  buildIndexDoc,
} from '@paradigm/terminal-core';
import type {
  AgentConnectionStatus,
  AgentWebSocket,
  ChatMessage,
  ChatSession,
  CustomFolder,
  GatewayFrame,
  ModelInfo,
  SessionFolder,
  TerminalSync,
} from '@paradigm/terminal-core';
import { fetchGatewayModels } from '../models/gateway-models';
import { defaultModelLabel } from '../models/model-labels';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

import { useTerminalConfig } from '../context/config-context';
import { buildTerminalStores } from '../config';
import { buildSendPayload } from '../send-payload';
import {
  AgentActionsProvider,
  AgentStateProvider,
  INITIAL_AGENT_STATE,
  type AgentActions,
  type AgentState,
} from './contexts';
import { handleFrame } from './handleFrame';

interface Props {
  readonly children: ReactNode;
}

const DEFAULT_SESSION_TITLE = 'New Chat';

/**
 * Resolve an incoming frame's `sessionKey` to one of the UI's known session
 * ids. The gateway echoes the key namespaced as `agent:<channel>:<uiSessionId>`
 * (e.g. `agent:main:<uuid>`, `agent:cron:<uuid>`), but the UI tracks bare
 * session ids. So we:
 *   1. Prefer a whole-key exact match — an id that arrives un-namespaced (even
 *      one that itself contains colons) wins outright and is never split.
 *   2. Otherwise strip the `agent:<channel>:` prefix and match what remains.
 *      We keep EVERYTHING after the channel segment (not just the trailing
 *      `:`-segment), so an id containing colons still resolves and a prefix
 *      collision like ids `{'id', 'session:id'}` with key `agent:main:session:id`
 *      resolves to `'session:id'`, never the shorter `'id'`.
 * Returns the matched session id, or null when nothing resolves (the caller
 * then falls back to run-ownership / last-send heuristics).
 */
export function matchKnownSessionKey(
  rawKey: string | null | undefined,
  knownSessionIds: ReadonlySet<string>,
): string | null {
  if (rawKey == null) return null;
  if (knownSessionIds.has(rawKey)) return rawKey;
  const parts = rawKey.split(':');
  if (parts[0] === 'agent' && parts.length >= 3) {
    const sessionId = parts.slice(2).join(':');
    if (sessionId.length > 0 && knownSessionIds.has(sessionId)) {
      return sessionId;
    }
  }
  return null;
}

function deriveSessionTitle(content: string): string {
  const oneLine = content.replace(/\s+/gu, ' ').trim();
  if (oneLine.length <= SESSION_TITLE_MAX_LEN) return oneLine;
  return `${oneLine.slice(0, SESSION_TITLE_MAX_LEN - 1)}…`;
}

/**
 * Pick which session should be active after hydration. Prefer the
 * persisted `activeSessionId` (still valid only if the session still
 * exists), otherwise the most-recently-updated session, otherwise null.
 */
function resolveActiveSession(
  sessions: readonly ChatSession[],
  persistedActiveId: string | null,
): string | null {
  if (sessions.length === 0) return null;
  if (
    persistedActiveId != null &&
    sessions.some((s) => s.sessionId === persistedActiveId)
  ) {
    return persistedActiveId;
  }
  const mostRecent = [...sessions].sort((a, b) => b.updatedAt - a.updatedAt)[0];
  return mostRecent?.sessionId ?? null;
}

function closeStreamingMessages(
  messages: readonly ChatMessage[],
): readonly ChatMessage[] {
  if (!messages.some((m) => m.isStreaming === true)) return messages;
  return messages.map((m) =>
    m.isStreaming === true ? { ...m, isStreaming: false } : m,
  );
}

let nextMsgId = Date.now();

// Storage bucket sent to the sync server as `X-Sync-User`. A single shared
// value across every surface (standalone / Paradex / Paradigm) so sessions are
// identical once they all embed this package. Token-mode gateways key storage
// on this header; trusted-proxy gateways ignore it (they use the wallet
// address). Auth itself stays on the gateway token for now.
const SYNC_USER = 'default';

// Restore (snapshot pull) retry policy. Restore fires when the connection
// reaches `authenticated`; a null result is either a transient failure or a
// gateway without `/sync/v1`. We retry a bounded number of times so transient
// blips recover while a no-sync gateway settles to local-only quickly.
const RESTORE_MAX_ATTEMPTS = 4;
const RESTORE_RETRY_BASE_MS = 1_000;

function buildSessionContextMessage(
  sessionContext: Record<string, string>,
): string {
  const body = Object.entries(sessionContext)
    .map(([k, v]) => `${k}=${v}`)
    .join(';');
  return `<session-context>${body}</session-context>`;
}

/**
 * Whether the terminal has enough to show (vs. its login screen). A gateway URL
 * is always required. Token-only hosts (`supportsPairingRole === false`, e.g.
 * Paradigm) additionally need a token — stored, or supplied via config
 * `fallbackPairingToken`. Pairing-role hosts self-pair from the URL alone, so a
 * token isn't required up front.
 */
function deriveIsPaired(opts: {
  readonly account: string | null;
  readonly url: string | null;
  readonly supportsPairingRole?: boolean;
  readonly hasStoredToken: boolean;
  readonly fallbackPairingToken?: string;
}): boolean {
  if (opts.account == null) return false;
  if (opts.url == null || opts.url.length === 0) return false;
  if (opts.supportsPairingRole !== false) return true;
  return opts.hasStoredToken || (opts.fallbackPairingToken?.length ?? 0) > 0;
}

export function AgentProvider({ children }: Props) {
  const config = useTerminalConfig();

  // Stores are stable per namespace; the component memoizes on that key.
  const { urlStore, historyStore, deviceStore } = useMemo(
    () => buildTerminalStores(config),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.namespace],
  );

  const accountId = config.accountId;
  const defaultFolder = (config.defaultFolder ??
    config.namespace) as SessionFolder;

  const accountIdRef = useRef<string | null>(accountId);
  accountIdRef.current = accountId;

  // Seed URL + pairing synchronously from storage/config, before the async
  // hydrate effect runs, so (1) a returning paired user goes straight to the
  // terminal with no login flash, and (2) the login screen prefills the known
  // gateway URL — matching what the settings screen shows.
  //
  // `agentUrl` is seeded ONLY when unpaired (login screen). When paired, we
  // leave it null and let hydrate set it alongside the initial connect;
  // seeding it here would make ChatInterface's mount-connect fire a duplicate.
  const [state, setState] = useState<AgentState>(() => {
    const initialUrl =
      accountId != null
        ? (urlStore.getAgentUrl(accountId) ?? config.gatewayUrl ?? null)
        : null;
    const initialPaired = deriveIsPaired({
      account: accountId,
      url: initialUrl,
      supportsPairingRole: config.supportsPairingRole,
      hasStoredToken:
        accountId != null && urlStore.getAgentToken(accountId) != null,
      fallbackPairingToken: config.fallbackPairingToken,
    });
    return {
      ...INITIAL_AGENT_STATE,
      agentUrl: initialPaired ? null : initialUrl,
      isPaired: initialPaired,
    };
  });
  const wsRef = useRef<AgentWebSocket | null>(null);

  // Stash the latest config bits behind refs so the memoized `connect`
  // closure always sees current values without being re-created on
  // every render.
  const configRef = useRef(config);
  configRef.current = config;

  const generativeUiEnabledRef = useRef(config.features?.generativeUi ?? false);
  generativeUiEnabledRef.current = config.features?.generativeUi ?? false;

  const reportError = useCallback((err: unknown) => {
    configRef.current.logException?.(err);
  }, []);

  // Remember the last URL we pushed to the consumer's persistence so
  // repeated reconnects with the same URL don't spam the callback.
  const lastPersistedUrlRef = useRef<string | null>(null);

  // Track the identity we've already sent a `<session-context>` for over
  // the current WS instance, so heartbeat-driven reconnects don't repeatedly
  // re-introduce the operator. Cleared whenever the socket drops below
  // 'authenticated', so the *next* successful auth always re-sends.
  const sessionContextSentForRef = useRef<string | null>(null);

  // Per-run session ownership: runId → sessionId.
  const runOwnershipRef = useRef<Map<string, string>>(new Map());

  // In-memory message buffer for sessions that are receiving frames while
  // not active.
  const backgroundSessionsRef = useRef<Map<string, readonly ChatMessage[]>>(
    new Map(),
  );

  // Parallel buffer for the "Thinking" indicator of inactive sessions. Without
  // it, the user switching back to an in-flight chat sees their question but
  // no loading state — the work IS still in flight, but the UI signal vanishes
  // because `processing` only lives on the live state object.
  const backgroundProcessingRef = useRef<Map<string, AgentState['processing']>>(
    new Map(),
  );

  const activeSessionIdRef = useRef<string | null>(null);
  activeSessionIdRef.current = state.activeSessionId;

  const cancelledRunIdsRef = useRef<ReadonlySet<string>>(new Set<string>());
  cancelledRunIdsRef.current = state.cancelledRunIds;

  const suppressedModelEchoRunIdsRef = useRef<Set<string>>(new Set());

  const selectedModelIdRef = useRef<string | null>(null);
  selectedModelIdRef.current = state.selectedModelId;
  const statusRef = useRef<AgentConnectionStatus>('disconnected');
  statusRef.current = state.status;

  const knownSessionIdsRef = useRef<Set<string>>(new Set());
  knownSessionIdsRef.current = new Set(state.sessions.map((s) => s.sessionId));

  const lastSendSessionRef = useRef<string | null>(null);

  const messagesRef = useRef<readonly ChatMessage[]>([]);
  messagesRef.current = state.messages;
  const processingRef = useRef<AgentState['processing']>(null);
  processingRef.current = state.processing;

  /**
   * Look up the session that owns `payload.runId`, lazily registering it
   * the first time we see a frame for a new run.
   */
  const resolveOwner = useCallback(
    (payload: { sessionKey?: string; runId?: string }): string | null => {
      const ourSessionKey = matchKnownSessionKey(
        payload.sessionKey,
        knownSessionIdsRef.current,
      );
      if (ourSessionKey != null && payload.runId != null) {
        runOwnershipRef.current.set(payload.runId, ourSessionKey);
      } else if (
        payload.runId != null &&
        !runOwnershipRef.current.has(payload.runId) &&
        lastSendSessionRef.current != null &&
        knownSessionIdsRef.current.has(lastSendSessionRef.current)
      ) {
        runOwnershipRef.current.set(payload.runId, lastSendSessionRef.current);
      }
      return (
        ourSessionKey ??
        (payload.runId != null
          ? (runOwnershipRef.current.get(payload.runId) ?? null)
          : null)
      );
    },
    [],
  );

  /**
   * Resolve which session a frame belongs to and apply a message reducer
   * either to live state (active session) or to a background buffer.
   */
  const routeMessageFrame = useCallback(
    (
      payload: { sessionKey?: string; runId?: string },
      reducer: (messages: readonly ChatMessage[]) => readonly ChatMessage[],
      activeExtra?: {
        processing?: { label: string; detail?: string } | null;
      },
    ): void => {
      const targetId = resolveOwner(payload);
      const activeId = activeSessionIdRef.current;

      if (targetId == null || targetId === activeId) {
        setState((prev) => {
          const next = reducer(prev.messages);
          if (
            next === prev.messages &&
            (activeExtra == null || !('processing' in activeExtra))
          ) {
            return prev;
          }
          return {
            ...prev,
            messages: next,
            ...(activeExtra != null && 'processing' in activeExtra
              ? { processing: activeExtra.processing ?? null }
              : {}),
          };
        });
        return;
      }

      const account = accountIdRef.current;
      if (account == null) return;

      const existing =
        backgroundSessionsRef.current.get(targetId) ??
        historyStore.loadSessionMessages(account, targetId);
      const next = reducer(existing);
      if (next === existing) return;

      backgroundSessionsRef.current.set(targetId, next);
      // Once real content lands in a background session, it's no longer
      // "Thinking" — drop the stashed indicator so a switch-back doesn't show
      // both a streaming bubble AND the verb spinner.
      if (activeExtra != null && 'processing' in activeExtra) {
        if (activeExtra.processing == null) {
          backgroundProcessingRef.current.delete(targetId);
        } else {
          backgroundProcessingRef.current.set(targetId, activeExtra.processing);
        }
      }
      setState((prev) => ({
        ...prev,
        sessions: prev.sessions.map((s) =>
          s.sessionId === targetId ? { ...s, updatedAt: Date.now() } : s,
        ),
      }));

      const stillStreaming = next.some((m) => m.isStreaming === true);
      if (!stillStreaming) {
        historyStore.saveSessionMessages(account, targetId, next);
        backgroundSessionsRef.current.delete(targetId);
        backgroundProcessingRef.current.delete(targetId);
      }
    },
    [resolveOwner, historyStore],
  );

  const connect = useCallback(
    (url?: string, token?: string) => {
      const account = accountIdRef.current;
      if (account == null) return;

      const cfg = configRef.current;

      // A caller-supplied pairing token (e.g. the setup form) is persisted to
      // the token store before the socket opens, so the handshake sends it as
      // `auth.token`. Drop any cached device token from a prior pairing so the
      // new token is used instead of a stale one (avoids "token mismatch").
      if (token != null && token.trim().length > 0) {
        urlStore.setAgentToken(account, token.trim());
        urlStore.clearDeviceToken(account);
      }

      // resolvedUrl precedence: explicit arg / config.gatewayUrl, then the
      // host resolver, then the localStorage-backed urlStore.
      void (async () => {
        const explicitUrl = url ?? cfg.gatewayUrl ?? null;
        const resolvedFromHost =
          explicitUrl ?? (await cfg.resolveUrl?.(account)) ?? null;
        const agentUrl =
          resolvedFromHost ?? urlStore.getAgentUrl(account) ?? null;
        if (agentUrl == null || agentUrl.length === 0) return;

        // Close existing connection
        if (wsRef.current != null) {
          wsRef.current.close();
        }

        setState((prev) => ({
          ...prev,
          agentUrl,
          status: 'connecting',
          isPaired: deriveIsPaired({
            account,
            url: agentUrl,
            supportsPairingRole: cfg.supportsPairingRole,
            hasStoredToken: urlStore.getAgentToken(account) != null,
            fallbackPairingToken: cfg.fallbackPairingToken,
          }),
        }));

        const ws = createOpenClawClient(agentUrl, {
          clientIdPrefix: cfg.clientIdPrefix,
          accountId: account,
          tokenProvider: urlStore,
          deviceSigner: deviceStore,
          healthStrategy: cfg.healthStrategy ?? 'heartbeat',
          supportsPairingRole: cfg.supportsPairingRole ?? true,
          catalogManifest: cfg.features?.generativeUi
            ? cfg.catalogManifest
            : undefined,
          fallbackPairingToken: cfg.fallbackPairingToken,
          onDebug: cfg.onConnectionDebug,
        });
        wsRef.current = ws;

        ws.onStatusChange((newStatus, errorMsg) => {
          setState((prev) => ({
            ...prev,
            status: newStatus,
            errorMessage:
              errorMsg ?? (newStatus === 'error' ? prev.errorMessage : null),
          }));

          // Reset the session-context guard whenever we leave 'authenticated'
          // so the next successful auth re-sends the operator identity.
          if (newStatus !== 'authenticated') {
            sessionContextSentForRef.current = null;
          }

          if (newStatus === 'authenticated') {
            // One-time session-context message so the agent knows the
            // operator identity. Bypasses sendMessage so it doesn't appear
            // in visible chat history.
            const dedupKey = cfg.sessionContextDedupKey ?? account;
            const ctx = cfg.sessionContext;
            if (ctx != null && sessionContextSentForRef.current !== dedupKey) {
              try {
                ws.send(buildSessionContextMessage(ctx));
                sessionContextSentForRef.current = dedupKey;
              } catch (err) {
                reportError(err);
              }
            }

            // Fetch recent chat history so reconnects show prior messages.
            fetchChatHistory(ws, setState);

            // Re-sync the user's model pick to the gateway (per-session).
            const persistedModelId = selectedModelIdRef.current;
            if (persistedModelId != null) {
              try {
                ws.send(`/model ${persistedModelId}`, {
                  sessionKey: activeSessionIdRef.current ?? undefined,
                });
              } catch (err) {
                reportError(err);
              }
            }

            // Notify consumer so they can persist the successfully-paired
            // URL + cache it locally. Guard so reconnects with the same URL
            // don't hammer their persistence layer.
            if (
              agentUrl.length > 0 &&
              agentUrl !== lastPersistedUrlRef.current
            ) {
              lastPersistedUrlRef.current = agentUrl;
              try {
                cfg.persistAgentUrl?.(agentUrl);
                urlStore.setAgentUrl(account, agentUrl);
              } catch (err) {
                lastPersistedUrlRef.current = null;
                reportError(err);
              }
            }
          }
        });

        ws.onMessage((frame: GatewayFrame) => {
          handleFrame(frame, setState, {
            generativeUiEnabled: generativeUiEnabledRef.current,
            onAppRegister: undefined,
            onAppRemove: undefined,
            suppressedModelEchoRunIds: suppressedModelEchoRunIdsRef.current,
            routing: {
              routeMessageFrame,
              isCancelled: (runId) =>
                runId != null && cancelledRunIdsRef.current.has(runId),
              isFrameForActiveSession: (payload) => {
                const ownerId = resolveOwner(payload);
                return (
                  ownerId == null || ownerId === activeSessionIdRef.current
                );
              },
              onRunEnd: (runId) => {
                runOwnershipRef.current.delete(runId);
                suppressedModelEchoRunIdsRef.current.delete(runId);
              },
            },
          });
        });
      })().catch(reportError);
    },
    [reportError, routeMessageFrame, resolveOwner, urlStore, deviceStore],
  );

  const disconnect = useCallback(() => {
    const account = accountIdRef.current;
    const cfg = configRef.current;
    if (wsRef.current != null) {
      wsRef.current.close();
      wsRef.current = null;
    }
    // Token-only hosts (no self-service pairing): drop the stored token so the
    // disconnect is durable. A later remount re-reads storage, finds no token,
    // and shows the login screen instead of silently reconnecting. Pairing-role
    // hosts keep their URL-based pairing — there's nothing to drop.
    if (account != null && cfg.supportsPairingRole === false) {
      urlStore.clearAgentToken(account);
    }
    setState((prev) => ({
      ...prev,
      status: 'disconnected',
      processing: null,
      isPaired: deriveIsPaired({
        account,
        url: prev.agentUrl,
        supportsPairingRole: cfg.supportsPairingRole,
        hasStoredToken:
          account != null && urlStore.getAgentToken(account) != null,
        fallbackPairingToken: cfg.fallbackPairingToken,
      }),
    }));
  }, [urlStore]);

  const sendMessage = useCallback(
    (content: string, opts?: { readonly context?: string }) => {
      if (wsRef.current == null) return;

      const userMsg: ChatMessage = {
        id: `user-${nextMsgId++}`,
        role: 'user',
        content,
        timestamp: Date.now(),
      };

      const resolved: { id: string | null } = { id: null };
      setState((prev) => {
        const now = Date.now();
        let { sessions } = prev;
        let activeId = prev.activeSessionId;

        if (activeId == null) {
          const newId = generateSessionId();
          const newSession: ChatSession = {
            sessionId: newId,
            folder: defaultFolder,
            title: DEFAULT_SESSION_TITLE,
            pinned: false,
            createdAt: now,
            updatedAt: now,
          };
          sessions = [newSession, ...sessions];
          activeId = newId;
        }

        resolved.id = activeId;

        sessions = sessions.map((s) =>
          s.sessionId === activeId
            ? {
                ...s,
                title:
                  s.title === DEFAULT_SESSION_TITLE
                    ? deriveSessionTitle(content)
                    : s.title,
                updatedAt: now,
              }
            : s,
        );

        return {
          ...prev,
          sessions,
          activeSessionId: activeId,
          processing: { label: 'Thinking' },
          messages: [...prev.messages, userMsg],
        };
      });

      // Prepend the host's system preamble (if any) and append optional
      // per-message hidden context to the gateway payload, but keep the chat
      // bubble (userMsg above) showing only what the user typed.
      const payload = buildSendPayload(
        configRef.current.systemPreamble,
        content,
        opts?.context,
      );

      lastSendSessionRef.current = resolved.id;
      wsRef.current.send(payload, {
        sessionKey: resolved.id ?? undefined,
      });
    },
    [defaultFolder],
  );

  const clearMessages = useCallback(() => {
    setState((prev) => ({ ...prev, messages: [], processing: null }));
  }, []);

  const createSession = useCallback(
    ({
      folder,
      isTemporary,
    }: {
      readonly folder: SessionFolder;
      readonly isTemporary?: boolean;
    }): string => {
      const id = generateSessionId();
      const now = Date.now();
      const newSession: ChatSession = {
        sessionId: id,
        folder,
        title: DEFAULT_SESSION_TITLE,
        pinned: false,
        createdAt: now,
        updatedAt: now,
        ...(isTemporary === true ? { isTemporary: true } : {}),
      };
      setState((prev) => {
        // Stash outgoing messages whenever the chat has work in flight — a
        // visibly streaming bubble OR a "Thinking" indicator (send already
        // happened, first delta not back yet). Without the second case, an
        // early switch loses the just-sent user message because the delta
        // arrives before the persist effect flushes it.
        const hasInFlight =
          prev.messages.some((m) => m.isStreaming === true) ||
          prev.processing != null;
        if (prev.activeSessionId != null && hasInFlight) {
          backgroundSessionsRef.current.set(prev.activeSessionId, [
            ...prev.messages,
          ]);
          if (prev.processing != null) {
            backgroundProcessingRef.current.set(
              prev.activeSessionId,
              prev.processing,
            );
          }
        }
        return {
          ...prev,
          sessions: [
            newSession,
            ...prev.sessions.filter((s) => s.isTemporary !== true),
          ],
          activeSessionId: id,
          messages: [],
          processing: null,
        };
      });
      return id;
    },
    [],
  );

  // Lazy-load a session's transcript from the gateway when it isn't cached
  // locally (e.g. a chat created on another device — the restore snapshot only
  // bundles the most-recent transcripts; the long tail loads on open). No-ops
  // when local already has messages, the gateway has no `/sync/v1`, or the user
  // switched away before it resolved.
  const ensureRemoteTranscript = useCallback(
    (sessionId: string) => {
      const account = accountIdRef.current;
      const sync = syncRef.current;
      if (account == null || sync == null) return;
      if (historyStore.loadSessionMessages(account, sessionId).length > 0) {
        return;
      }
      fetchAndMergeSessionFromRemote(historyStore, account, sync, sessionId)
        .then((merged) => {
          if (merged == null || merged.length === 0) return;
          if (accountIdRef.current !== account) return;
          setState((prev) =>
            prev.activeSessionId === sessionId
              ? { ...prev, messages: merged }
              : prev,
          );
        })
        .catch(reportError);
    },
    [historyStore, reportError],
  );

  const switchSession = useCallback(
    (sessionId: string) => {
      const account = accountIdRef.current;
      if (account == null) return;
      const cached = backgroundSessionsRef.current.get(sessionId);
      const cachedProcessing =
        backgroundProcessingRef.current.get(sessionId) ?? null;
      const messages =
        cached != null
          ? [...cached]
          : historyStore.loadSessionMessages(account, sessionId);
      if (cached != null) {
        backgroundSessionsRef.current.delete(sessionId);
      }
      backgroundProcessingRef.current.delete(sessionId);
      // Pull the transcript from the gateway if we opened a session we have no
      // local copy of (skip when restoring a still-streaming background chat).
      if (cached == null) ensureRemoteTranscript(sessionId);
      setState((prev) => {
        if (!prev.sessions.some((s) => s.sessionId === sessionId)) return prev;
        // Stash outgoing messages whenever the chat has work in flight — a
        // visibly streaming bubble OR a "Thinking" indicator (send already
        // happened, first delta not back yet). Without the second case, an
        // early switch loses the just-sent user message because the delta
        // arrives before the persist effect flushes it.
        const hasInFlight =
          prev.messages.some((m) => m.isStreaming === true) ||
          prev.processing != null;
        if (
          prev.activeSessionId != null &&
          prev.activeSessionId !== sessionId &&
          hasInFlight
        ) {
          backgroundSessionsRef.current.set(prev.activeSessionId, [
            ...prev.messages,
          ]);
          if (prev.processing != null) {
            backgroundProcessingRef.current.set(
              prev.activeSessionId,
              prev.processing,
            );
          }
        }
        return {
          ...prev,
          sessions: prev.sessions.filter(
            (s) => s.isTemporary !== true || s.sessionId === sessionId,
          ),
          activeSessionId: sessionId,
          messages,
          // Restore the indicator if the session has work pending — switching
          // back during "Thinking" should still show the verb spinner.
          processing: cachedProcessing,
        };
      });
    },
    [historyStore, ensureRemoteTranscript],
  );

  const renameSession = useCallback((sessionId: string, title: string) => {
    const trimmed = title.trim();
    if (trimmed.length === 0) return;
    setState((prev) => ({
      ...prev,
      sessions: prev.sessions.map((s) =>
        s.sessionId === sessionId
          ? { ...s, title: trimmed.slice(0, SESSION_TITLE_MAX_LEN) }
          : s,
      ),
    }));
  }, []);

  const pinSession = useCallback((sessionId: string, pinned: boolean) => {
    setState((prev) => ({
      ...prev,
      sessions: prev.sessions.map((s) =>
        s.sessionId === sessionId ? { ...s, pinned } : s,
      ),
    }));
  }, []);

  const stopProcessing = useCallback(() => {
    const currentMessages = messagesRef.current;
    const hasStreaming = currentMessages.some((m) => m.isStreaming === true);
    if (processingRef.current == null && !hasStreaming) return;

    const lastStreaming = [...currentMessages]
      .reverse()
      .find((m) => m.isStreaming === true);
    const runId = lastStreaming?.runId;

    if (wsRef.current != null) {
      wsRef.current
        .request('chat.cancel', runId != null ? { runId } : undefined)
        .catch(() => {
          // Gateway doesn't implement chat.cancel — fine.
        });
    }

    setState((prev) => {
      const hasStreamingNow = prev.messages.some((m) => m.isStreaming === true);
      if (prev.processing == null && !hasStreamingNow) return prev;

      const cancelledIds = new Set(prev.cancelledRunIds);
      for (const m of prev.messages) {
        if (m.isStreaming === true && m.runId != null) {
          cancelledIds.add(m.runId);
        }
      }

      const stoppedMsg: ChatMessage = {
        id: `stopped-${nextMsgId++}`,
        role: 'assistant' as const,
        content: '_Stopped._',
        timestamp: Date.now(),
      };

      return {
        ...prev,
        processing: null,
        messages: [...closeStreamingMessages(prev.messages), stoppedMsg],
        cancelledRunIds: cancelledIds,
      };
    });
  }, []);

  const deleteSession = useCallback(
    (sessionId: string) => {
      const account = accountIdRef.current;
      if (account != null) {
        historyStore.deleteSessionMessages(account, sessionId);
        // Record a tombstone so the deletion survives a sync merge (otherwise
        // the session resurrects from another device's index) and push it.
        historyStore.addTombstone(account, 'session', sessionId);
        syncRef.current?.queueDeleteSession(sessionId);
        // The sessions-index queue happens via the persistence effect below
        // when `state.sessions` updates (the tombstone is now in the store).
      }
      setState((prev) => {
        const remaining = prev.sessions.filter(
          (s) => s.sessionId !== sessionId,
        );
        if (prev.activeSessionId !== sessionId) {
          return { ...prev, sessions: remaining };
        }
        if (remaining.length === 0) {
          const now = Date.now();
          const fresh: ChatSession = {
            sessionId: generateSessionId(),
            folder: defaultFolder,
            title: DEFAULT_SESSION_TITLE,
            pinned: false,
            createdAt: now,
            updatedAt: now,
          };
          return {
            ...prev,
            sessions: [fresh],
            activeSessionId: fresh.sessionId,
            messages: [],
            processing: null,
          };
        }
        const next = [...remaining].sort(
          (a, b) => b.updatedAt - a.updatedAt,
        )[0]!;
        const nextMessages =
          account != null
            ? historyStore.loadSessionMessages(account, next.sessionId)
            : [];
        return {
          ...prev,
          sessions: remaining,
          activeSessionId: next.sessionId,
          messages: nextMessages,
          processing: null,
        };
      });
    },
    [defaultFolder, historyStore],
  );

  const setSessionFolder = useCallback(
    (sessionId: string, customFolderId: string | undefined) => {
      // Stamp `folderUpdatedAt` (NOT `updatedAt` — a move isn't chat
      // activity and must not resurface the chat in Recents) so the sync
      // merge propagates the move instead of losing it to any record
      // that's fresher by `updatedAt` on another device.
      const folderUpdatedAt = Date.now();
      setState((prev) => ({
        ...prev,
        sessions: prev.sessions.map((s) =>
          s.sessionId === sessionId
            ? customFolderId == null
              ? (() => {
                  const { customFolderId: _drop, ...rest } = s;
                  return { ...rest, folderUpdatedAt } as ChatSession;
                })()
              : { ...s, customFolderId, folderUpdatedAt }
            : s,
        ),
      }));
    },
    [],
  );

  const createCustomFolder = useCallback((name: string): string => {
    const trimmed = name.trim();
    if (trimmed.length === 0) return '';
    const folder: CustomFolder = {
      id: generateSessionId(),
      name: trimmed,
      createdAt: Date.now(),
    };
    setState((prev) => ({
      ...prev,
      customFolders: [...prev.customFolders, folder],
    }));
    return folder.id;
  }, []);

  const renameCustomFolder = useCallback((folderId: string, name: string) => {
    const trimmed = name.trim();
    if (trimmed.length === 0) return;
    setState((prev) => ({
      ...prev,
      customFolders: prev.customFolders.map((f) =>
        f.id === folderId ? { ...f, name: trimmed } : f,
      ),
    }));
  }, []);

  const pinCustomFolder = useCallback((folderId: string, pinned: boolean) => {
    setState((prev) => ({
      ...prev,
      customFolders: prev.customFolders.map((f) => {
        if (f.id !== folderId) return f;
        if (pinned) return { ...f, pinned: true };
        const { pinned: _drop, ...rest } = f;
        return rest as CustomFolder;
      }),
    }));
  }, []);

  const deleteCustomFolder = useCallback(
    (folderId: string) => {
      const account = accountIdRef.current;
      if (account != null) {
        // Record a folder tombstone so the deletion survives a sync merge
        // — without one, `mergeIndex` resurrects the folder (empty) from
        // any other device's index. Uploaded via the persistence effect's
        // `queueIndex` (tombstones ride in the index doc).
        historyStore.addTombstone(account, 'folder', folderId);
      }
      // The unfiling of member sessions is a folder-assignment change like
      // any other — stamp it so it wins sync merges (see setSessionFolder).
      const folderUpdatedAt = Date.now();
      setState((prev) => ({
        ...prev,
        customFolders: prev.customFolders.filter((f) => f.id !== folderId),
        sessions: prev.sessions.map((s) => {
          if (s.customFolderId !== folderId) return s;
          const { customFolderId: _drop, ...rest } = s;
          return { ...rest, folderUpdatedAt } as ChatSession;
        }),
      }));
    },
    [historyStore],
  );

  const respondToApproval = useCallback(
    (approvalId: string, approved: boolean) => {
      setState((prev) => ({
        ...prev,
        messages: prev.messages.map((msg) =>
          msg.approval?.approvalId === approvalId
            ? {
                ...msg,
                approval: {
                  ...msg.approval,
                  state: approved ? ('approved' as const) : ('denied' as const),
                },
              }
            : msg,
        ),
      }));
      wsRef.current
        ?.request('approval.respond', { approvalId, approved })
        .catch(reportError);
    },
    [reportError],
  );

  const request: AgentActions['request'] = useCallback(
    async <T,>(
      method: string,
      params?: Record<string, unknown>,
    ): Promise<T> => {
      if (wsRef.current == null) {
        throw new Error('Not connected');
      }
      return wsRef.current.request<T>(method, params);
    },
    [],
  );

  const requestPairing = useCallback(async () => {
    if (wsRef.current == null) {
      throw new Error('Not connected');
    }
    return wsRef.current.requestPairing();
  }, []);

  const sendUiEvent = useCallback(
    (params: {
      readonly surfaceId: string;
      readonly componentId?: string;
      readonly event: string;
      readonly data?: Record<string, unknown>;
    }) => {
      wsRef.current?.sendUiEvent(params);
    },
    [],
  );

  const dismissFullTerminalUi = useCallback(() => {
    setState((prev) => ({ ...prev, fullTerminalUi: null }));
  }, []);

  const setSelectedModel = useCallback(
    (modelId: string) => {
      const account = accountIdRef.current;
      const label = defaultModelLabel(modelId);
      const isAuthenticated = statusRef.current === 'authenticated';

      setState((prev) => ({
        ...prev,
        selectedModelId: modelId,
        messages: isAuthenticated
          ? [
              ...prev.messages,
              {
                id: `model-pick-${nextMsgId++}`,
                role: 'assistant' as const,
                content: `Model set to ${label} for this session.`,
                timestamp: Date.now(),
              },
            ]
          : prev.messages,
      }));
      if (account != null) urlStore.setSelectedModel(account, modelId);

      if (isAuthenticated && wsRef.current != null) {
        wsRef.current.send(`/model ${modelId}`, {
          sessionKey: activeSessionIdRef.current ?? undefined,
        });
      }
    },
    [urlStore],
  );

  const actions = useMemo<AgentActions>(
    () => ({
      sendMessage,
      connect,
      disconnect,
      clearMessages,
      respondToApproval,
      request,
      requestPairing,
      sendUiEvent,
      dismissFullTerminalUi,
      createSession,
      switchSession,
      renameSession,
      pinSession,
      deleteSession,
      setSessionFolder,
      createCustomFolder,
      renameCustomFolder,
      pinCustomFolder,
      deleteCustomFolder,
      stopProcessing,
      setSelectedModel,
    }),
    [
      sendMessage,
      connect,
      disconnect,
      clearMessages,
      respondToApproval,
      request,
      requestPairing,
      sendUiEvent,
      dismissFullTerminalUi,
      createSession,
      switchSession,
      renameSession,
      pinSession,
      deleteSession,
      setSessionFolder,
      createCustomFolder,
      renameCustomFolder,
      pinCustomFolder,
      deleteCustomFolder,
      stopProcessing,
      setSelectedModel,
    ],
  );

  // React to account switches: tear down the current session and
  // auto-reconnect if the new account has a stored or resolvable URL.
  const hydratedAccountRef = useRef<string | null>(null);

  // Native session-sync client for the hydrated account (null when the gateway
  // URL is unusable). Disposed + recreated on account/gateway change.
  const syncRef = useRef<TerminalSync | null>(null);
  const hydrate = useCallback(() => {
    const account = accountIdRef.current;

    // Signed-out: reset everything.
    if (account == null) {
      if (wsRef.current != null) {
        wsRef.current.close();
        wsRef.current = null;
      }
      hydratedAccountRef.current = null;
      setState({ ...INITIAL_AGENT_STATE });
      return;
    }

    // Already hydrated this account — config updates that arrive later must
    // not tear down the live session.
    if (hydratedAccountRef.current === account) {
      return;
    }

    const cfg = configRef.current;

    void (async () => {
      // localStorage is authoritative for the active browser; fall back to
      // explicit gatewayUrl / the host resolver.
      const storedUrl = urlStore.getAgentUrl(account);
      const resolved =
        cfg.gatewayUrl ?? (await cfg.resolveUrl?.(account)) ?? null;
      const effectiveUrl = storedUrl ?? resolved;

      // Re-check the account hasn't changed underneath the await.
      if (accountIdRef.current !== account) return;

      if (effectiveUrl == null || effectiveUrl.length === 0) {
        if (wsRef.current != null) {
          wsRef.current.close();
          wsRef.current = null;
        }
        hydratedAccountRef.current = null;
        setState({ ...INITIAL_AGENT_STATE });
        return;
      }

      if (wsRef.current != null) {
        wsRef.current.close();
        wsRef.current = null;
      }
      hydratedAccountRef.current = account;

      // Hydrate sessions + the active session's messages from localStorage.
      const migratedSessions =
        historyStore.migrateLegacyHistoryIfNeeded(account);
      const { sessions: hydratedSessions, recovered } =
        historyStore.recoverOrphanedSessions(account, migratedSessions);
      if (recovered) {
        historyStore.saveSessions(account, hydratedSessions);
      }
      const uiState = historyStore.loadHistoryUiState(account);
      const activeSessionId = resolveActiveSession(
        hydratedSessions,
        uiState.activeSessionId,
      );
      const persistedMessages =
        activeSessionId != null
          ? historyStore.loadSessionMessages(account, activeSessionId)
          : [];
      const persistedFolders = historyStore.loadCustomFolders(account);
      const { folders: hydratedFolders, recovered: foldersRecovered } =
        historyStore.recoverOrphanedFolders(hydratedSessions, persistedFolders);
      if (foldersRecovered) {
        historyStore.saveCustomFolders(account, hydratedFolders);
      }
      // Seed the picker at hydration. Absent models capability ⇒ models=[].
      const hydratedModels: readonly ModelInfo[] =
        cfg.capabilities?.models?.getStaticModels(effectiveUrl) ?? [];
      const persistedModelId = urlStore.getSelectedModel(account);
      const hydratedSelectedModelId =
        persistedModelId != null &&
        hydratedModels.some((m) => m.id === persistedModelId)
          ? persistedModelId
          : (hydratedModels.find((m) => m.legacy !== true)?.id ??
            hydratedModels[0]?.id ??
            null);
      const paired = deriveIsPaired({
        account,
        url: effectiveUrl,
        supportsPairingRole: cfg.supportsPairingRole,
        hasStoredToken: urlStore.getAgentToken(account) != null,
        fallbackPairingToken: cfg.fallbackPairingToken,
      });
      setState({
        ...INITIAL_AGENT_STATE,
        agentUrl: effectiveUrl,
        isPaired: paired,
        sessions: hydratedSessions,
        customFolders: hydratedFolders,
        activeSessionId,
        messages: persistedMessages,
        models: hydratedModels,
        selectedModelId: hydratedSelectedModelId,
      });

      if (storedUrl == null) {
        // Cache the URL locally so subsequent reads go through the store.
        urlStore.setAgentUrl(account, effectiveUrl);
      }
      // Only open the socket when paired. Pairing-role hosts count a bare URL
      // as paired (the gateway opens a pairing role for `node.pair.request`);
      // token-only hosts need a token first — otherwise we'd fire a handshake
      // that just fails auth, so the login screen shows instead.
      if (paired) {
        connect(effectiveUrl);
      }

      // Live model catalog is fetched off the `authenticated` status (see the
      // model-fetch effect below), NOT here — the native `/api/models` is
      // token-gated like `/sync/v1`, and a fresh pairing only seeds the gateway
      // token after the handshake resolves.

      // Native session-sync CLIENT only. The restore (snapshot pull) is NOT
      // run here — it's driven by the connection reaching `authenticated` (see
      // the restore effect below), because on a fresh pairing the gateway
      // token isn't seeded until the handshake resolves, so a restore fired
      // now would no-op on missing auth and never retry. Writes queue against
      // this client as soon as it exists.
      syncRef.current?.dispose();
      syncRef.current = createSessionSync({
        store: historyStore,
        accountId: account,
        agentUrl: effectiveUrl,
        getAuthHeaders: () => {
          const token = urlStore.getAgentToken(account);
          if (token == null || token === '') return null;
          return {
            Authorization: `Bearer ${token}`,
            'X-Sync-User': SYNC_USER,
          };
        },
      });
    })().catch(reportError);
  }, [connect, urlStore, historyStore, reportError]);

  // Hydrate on mount + whenever the account or gateway config changes.
  useEffect(() => {
    hydrate();
  }, [config.accountId, config.gatewayUrl, hydrate]);

  // Re-hydrate when the host signals an account change.
  useEffect(() => {
    const onAccountChange = config.onAccountChange;
    if (onAccountChange == null) return undefined;
    return onAccountChange(() => {
      hydrate();
    });
  }, [config.onAccountChange, hydrate]);

  // Restore from the gateway once per authenticated connection. This is the
  // core of the rework: restore is driven by the `authenticated` status — NOT
  // fired at mount — so the gateway token is guaranteed seeded (a fresh
  // pairing only seeds it after the handshake resolves). The ref resets
  // whenever we leave `authenticated`, so a reconnect re-restores; a `null`
  // result (transient failure or a gateway without `/sync/v1`) retries a
  // bounded number of times before settling to local-only.
  const restoredConnectionRef = useRef(false);
  useEffect(() => {
    if (state.status !== 'authenticated') {
      restoredConnectionRef.current = false;
      return undefined;
    }
    const account = accountId;
    if (account == null) return undefined;
    if (hydratedAccountRef.current !== account) return undefined;
    if (restoredConnectionRef.current) return undefined;
    const sync = syncRef.current;
    if (sync == null) return undefined;
    restoredConnectionRef.current = true;

    let cancelled = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const run = () => {
      hydrateFromRemote(historyStore, account, sync)
        .then((result) => {
          if (cancelled || accountIdRef.current !== account) return;
          if (result == null) {
            attempt += 1;
            if (attempt < RESTORE_MAX_ATTEMPTS) {
              timer = setTimeout(run, RESTORE_RETRY_BASE_MS * attempt);
            }
            return;
          }
          setState((prev) => ({
            ...prev,
            sessions: result.sessions,
            customFolders: result.folders,
            // Reload the open transcript if the merge changed it.
            messages:
              prev.activeSessionId != null &&
              result.updatedSessionIds.has(prev.activeSessionId)
                ? historyStore.loadSessionMessages(
                    account,
                    prev.activeSessionId,
                  )
                : prev.messages,
          }));
          // The open session may not have been bundled in the snapshot — pull
          // its transcript lazily so the active chat isn't left empty.
          const active = activeSessionIdRef.current;
          if (active != null) ensureRemoteTranscript(active);
        })
        .catch(reportError);
    };
    run();
    return () => {
      cancelled = true;
      if (timer != null) clearTimeout(timer);
    };
  }, [
    state.status,
    accountId,
    historyStore,
    reportError,
    ensureRemoteTranscript,
  ]);

  // Live model catalog: fetch once per authenticated connection, mirroring the
  // restore effect — `/api/models` is token-gated like `/sync/v1`, so the
  // gateway token must be seeded (only true after the handshake). Authenticates
  // the same way as the sync client. The picker keeps its static list on `[]`.
  const modelsFetchedConnectionRef = useRef(false);
  useEffect(() => {
    if (state.status !== 'authenticated') {
      modelsFetchedConnectionRef.current = false;
      return undefined;
    }
    const account = accountId;
    if (account == null || hydratedAccountRef.current !== account)
      return undefined;
    if (modelsFetchedConnectionRef.current) return undefined;
    const url = state.agentUrl;
    if (url == null) return undefined;
    modelsFetchedConnectionRef.current = true;

    const fetchModels =
      configRef.current.capabilities?.models?.fetchAvailableModels ??
      fetchGatewayModels;
    let cancelled = false;
    fetchModels(url, {
      getAuthHeaders: () => {
        const token = urlStore.getAgentToken(account);
        if (token == null || token === '') return null;
        return { Authorization: `Bearer ${token}`, 'X-Sync-User': SYNC_USER };
      },
    })
      .then((live: readonly ModelInfo[]) => {
        if (
          cancelled ||
          accountIdRef.current !== account ||
          live.length === 0
        ) {
          return;
        }
        // The native /api/models reads openclaw.json, whose labels are the
        // region-stripped id (e.g. "anthropic.claude-opus-4-8") — not the
        // friendly "Opus 4.8". Prettify via the native id→label table, keeping
        // the endpoint's label for ids it doesn't know (defaultModelLabel
        // echoes the id back when unknown).
        const models = live.map((m) => {
          const pretty = defaultModelLabel(m.id);
          return pretty !== m.id ? { ...m, label: pretty } : m;
        });
        setState((prev) => ({ ...prev, models }));
      })
      .catch(reportError);
    return () => {
      cancelled = true;
    };
  }, [state.status, state.agentUrl, accountId, urlStore, reportError]);

  // Persist the active session's messages so a refresh has something to
  // show. Temporary chats are skipped.
  useEffect(() => {
    if (accountId == null) return;
    if (hydratedAccountRef.current !== accountId) return;
    if (state.activeSessionId == null) return;
    const active = state.sessions.find(
      (s) => s.sessionId === state.activeSessionId,
    );
    if (active?.isTemporary === true) return;
    historyStore.saveSessionMessages(
      accountId,
      state.activeSessionId,
      state.messages,
    );
    // Push the transcript (coalesced + debounced by the sync client, so
    // streaming deltas don't spam the gateway).
    syncRef.current?.queueSession(state.activeSessionId, {
      messages: state.messages,
    });
  }, [
    accountId,
    historyStore,
    state.activeSessionId,
    state.messages,
    state.sessions,
  ]);

  // Persist the sessions index whenever it changes.
  useEffect(() => {
    if (accountId == null) return;
    if (hydratedAccountRef.current !== accountId) return;
    historyStore.saveSessions(
      accountId,
      state.sessions.filter((s) => s.isTemporary !== true),
    );
    // Converge the synced index (sessions + folders + tombstones).
    syncRef.current?.queueIndex(
      buildIndexDoc(
        historyStore,
        accountId,
        state.sessions,
        state.customFolders,
      ),
    );
  }, [accountId, historyStore, state.sessions, state.customFolders]);

  // Persist the user's custom folders whenever they change.
  useEffect(() => {
    if (accountId == null) return;
    if (hydratedAccountRef.current !== accountId) return;
    historyStore.saveCustomFolders(accountId, state.customFolders);
    syncRef.current?.queueIndex(
      buildIndexDoc(
        historyStore,
        accountId,
        state.sessions,
        state.customFolders,
      ),
    );
  }, [accountId, historyStore, state.sessions, state.customFolders]);

  // Persist the active session id so a reload re-opens the same chat.
  const lastSavedActiveRef = useRef<string | null>(null);
  useEffect(() => {
    if (accountId == null) return;
    if (hydratedAccountRef.current !== accountId) return;
    if (lastSavedActiveRef.current === state.activeSessionId) return;
    lastSavedActiveRef.current = state.activeSessionId;
    const ui = historyStore.loadHistoryUiState(accountId);
    historyStore.saveHistoryUiState(accountId, {
      ...ui,
      activeSessionId: state.activeSessionId,
    });
  }, [accountId, historyStore, state.activeSessionId]);

  // Cleanup on unmount. Crucially, reset the hydration guard so a *re-mount*
  // (a genuine one, or React StrictMode's dev setup→cleanup→setup double-
  // invoke) re-runs `hydrate()` and reconnects. Without this reset, the
  // cleanup closes the socket but the re-mounted `hydrate()` short-circuits on
  // `hydratedAccountRef`, leaving the UI stuck on 'connecting' with a dead
  // socket (reproduces only on page refresh, where the component mounts fresh).
  useEffect(() => {
    return () => {
      wsRef.current?.close();
      wsRef.current = null;
      syncRef.current?.dispose();
      syncRef.current = null;
      hydratedAccountRef.current = null;
    };
  }, []);

  return (
    <AgentStateProvider value={state}>
      <AgentActionsProvider value={actions}>{children}</AgentActionsProvider>
    </AgentStateProvider>
  );
}

/** Shape of a single message in the `chat.history` RPC response. */
interface HistoryMessage {
  readonly role?: string;
  readonly content?: string | ReadonlyArray<{ type?: string; text?: string }>;
  readonly timestamp?: number;
}

/**
 * Maximum number of messages OpenClaw's `chat.history` will return in one
 * call. The RPC has no cursor pagination — this is the deepest tail we can
 * pull.
 */
const CHAT_HISTORY_GATEWAY_LIMIT = 1000;

/**
 * Fetch recent chat history from the gateway after authentication. In
 * single-session mode (no active UI session) this seeds the empty chat
 * with the gateway tail. In session mode we skip merging into a
 * user-managed session.
 */
function fetchChatHistory(
  ws: AgentWebSocket,
  setState: Dispatch<SetStateAction<AgentState>>,
): void {
  ws.request<{ messages?: readonly HistoryMessage[] }>('chat.history', {
    limit: CHAT_HISTORY_GATEWAY_LIMIT,
  })
    .then((res) => {
      const seeded = normalizeHistoryMessages(res.messages);
      if (seeded.length === 0) return;

      setState((prev) => {
        if (prev.activeSessionId != null) return prev;
        if (prev.messages.length === 0) {
          return { ...prev, messages: seeded };
        }
        const seen = new Set(prev.messages.map(messageDedupKey));
        const additions = seeded.filter((m) => !seen.has(messageDedupKey(m)));
        if (additions.length === 0) return prev;
        const merged = [...prev.messages, ...additions].sort(
          (a, b) => a.timestamp - b.timestamp,
        );
        return { ...prev, messages: merged };
      });
    })
    .catch(() => {
      // Gateway doesn't support chat.history — silently skip.
    });
}

function messageDedupKey(m: ChatMessage): string {
  return `${m.timestamp}|${m.role}|${m.content}`;
}

function normalizeHistoryMessages(
  raw: readonly HistoryMessage[] | undefined,
): ChatMessage[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.map((m: HistoryMessage, i: number) => {
    let text = '';
    if (typeof m.content === 'string') {
      text = m.content;
    } else if (Array.isArray(m.content)) {
      text = (m.content as ReadonlyArray<{ type?: string; text?: string }>)
        .filter((c) => c.type === 'text' && c.text != null)
        .map((c) => c.text!)
        .join('');
    }
    const msg: ChatMessage = {
      id: `history-${m.timestamp ?? 'x'}-${i}`,
      role: m.role === 'user' ? 'user' : 'assistant',
      content: text,
      timestamp: m.timestamp ?? Date.now(),
    };
    return msg;
  });
}
